import type { ErrorApi } from '@libreta/contratos';

const BASE = '/api';

/**
 * Error de la API con el mensaje para la persona. El backend siempre devuelve
 * `{ mensaje, codigo }` redactado para leerse en pantalla: el front no inventa mensajes.
 */
export class ErrorDeApi extends Error {
  constructor(
    mensaje: string,
    readonly codigo: string,
    readonly estado: number,
    readonly detalles?: unknown,
  ) {
    super(mensaje);
    this.name = 'ErrorDeApi';
  }
}

/**
 * El access token vive en memoria, no en localStorage: un XSS lee localStorage, no una
 * variable de módulo. La sesión larga está en una cookie httpOnly que manda el navegador.
 */
let token: string | null = null;
let renovando: Promise<string | null> | null = null;
let alPerderSesion: (() => void) | null = null;

export function guardarToken(nuevo: string | null): void {
  token = nuevo;
}

export function avisarSesionPerdida(fn: () => void): void {
  alPerderSesion = fn;
}

export function renovarSesion(): Promise<string | null> {
  // Si llegan cinco pedidos con el token vencido, se renueva una sola vez.
  renovando ??= fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
    .then(async (respuesta) => {
      if (!respuesta.ok) return null;
      const datos = (await respuesta.json()) as { accessToken: string };
      token = datos.accessToken;
      return token;
    })
    .catch(() => null)
    .finally(() => {
      renovando = null;
    });
  return renovando;
}

interface Opciones {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  cuerpo?: unknown;
  archivo?: FormData;
  reintentar?: boolean;
}

/** Una sola puerta al backend: renueva el token, normaliza el error, devuelve el dato. */
export async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const { metodo = 'GET', cuerpo, archivo, reintentar = true } = opciones;
  const respuesta = await fetch(`${BASE}${ruta}`, {
    method: metodo,
    credentials: 'include',
    headers: {
      ...(cuerpo !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: archivo ?? (cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined),
  });

  if (respuesta.status === 401 && reintentar && !ruta.startsWith('/auth/login')) {
    const nuevo = await renovarSesion();
    if (nuevo) return pedir<T>(ruta, { ...opciones, reintentar: false });
    alPerderSesion?.();
  }

  if (respuesta.status === 204) return undefined as T;
  const datos = await respuesta.json().catch(() => null);
  if (!respuesta.ok) {
    const error = (datos ?? {}) as Partial<ErrorApi>;
    throw new ErrorDeApi(
      error.mensaje ?? 'No se pudo completar la operación. Probá de nuevo.',
      error.codigo ?? 'ERROR',
      respuesta.status,
      error.detalles,
    );
  }
  return datos as T;
}

/** Construye una query string ignorando lo vacío. */
export function consulta(parametros: Record<string, string | number | boolean | undefined | null>): string {
  const pares = Object.entries(parametros).filter(([, valor]) => valor !== undefined && valor !== null && valor !== '');
  if (pares.length === 0) return '';
  return `?${new URLSearchParams(pares.map(([clave, valor]) => [clave, String(valor)])).toString()}`;
}

export async function subirArchivo(archivo: File): Promise<{ id: string; nombre: string }> {
  const formulario = new FormData();
  formulario.append('archivo', archivo);
  return pedir('/archivos', { metodo: 'POST', archivo: formulario });
}

export async function abrirArchivo(id: string): Promise<void> {
  const { url } = await pedir<{ url: string }>(`/archivos/${id}/url`);
  window.open(url, '_blank', 'noopener');
}
